<template>
  <div>
    <header class="modal-header bold pb25 weight-700 bg-cl-secondary">
      {{ $t('Register') }}
      <i
        slot="close"
        class="modal-close icon icon-close-new fs24"
        @click="close"
      />
    </header>

    <div class="modal-content c-bg-white pt30 cl-secondary">
      <form
        novalidate
        @submit.prevent="register"
      >
        <base-input
          id="email"
          v-model="email"
          class="mb10"
          type="email"
          name="email"
          autocomplete="email"
          focus
          :placeholder="$t('E-mail address *')"
          :validations="[
            {
              condition: !$v.email.required && $v.email.$error,
              text: $t('Field is required.')
            },
            {
              condition: !$v.email.email && $v.email.$error,
              text: $t('Please provide valid e-mail address.')
            }
          ]"
          @blur="$v.email.$touch()"
        />
        <base-input
          id="fullName"
          v-model="fullName"
          class="mb10"
          type="text"
          name="fullName"
          autocomplete="name"
          :placeholder="$t('Full name *')"
          :validations="[
            {
              condition: !$v.fullName.required && $v.fullName.$error,
              text: $t('Field is required.')
            },
            {
              condition: !$v.fullName.minLength,
              text: $t('Name must have at least 2 letters.')
            }
          ]"
          @blur="$v.fullName.$touch()"
        />
        <base-input
          id="new-password"
          ref="password"
          v-model="password"
          class="mb10"
          type="password"
          name="new-password"
          autocomplete="new-password"
          :placeholder="$t('Password *')"
          :validations="[
            {
              condition: !$v.password.required && $v.password.$error,
              text: $t('Field is required.')
            },
            {
              condition: !$v.password.minLength && $v.password.$error,
              text: $t('Password must have at least 8 letters.')
            }
          ]"
          @blur="$v.password.$touch()"
        />
        <base-input
          v-model="rPassword"
          class="mb32"
          type="password"
          name="password-confirm"
          autocomplete="new-password"
          :placeholder="$t('Repeat password *')"
          :validations="[
            {
              condition: !$v.rPassword.required && $v.rPassword.$error,
              text: $t('Field is required.')
            },
            {
              condition: !$v.rPassword.sameAsPassword && $v.rPassword.$error,
              text: $t('Passwords must be identical.')
            }
          ]"
          @blur="$v.rPassword.$touch()"
        />
        <base-checkbox
          id="terms"
          v-model="conditions"
          class="mb20 checkbox-label"
          :validations="[{
            condition: !$v.conditions.sameAs && $v.conditions.$error,
            text: $t('You must accept the terms and conditions.')
          }]"
          @blur="$v.conditions.$reset()"
          @change="$v.conditions.$touch()"
        >
          <span v-html="conditionsText" />
        </base-checkbox>
        <base-checkbox
          id="newsletter"
          v-model="newsletter"
          class="mb32 checkbox-label"
        >
          {{ $t('I want to receive event news and the opportunity to pre-purchase tickets to the most popular events.') }}
        </base-checkbox>
        <button-full
          class="mb20 button-primary"
          type="submit"
        >
          {{ $t('Register an account') }}
        </button-full>
        <div class="center-xs login-link">
          {{ $t('Already have account?') }}
          <br class="hidden-desktop hidden-tablet">
          <a
            href="#"
            data-testid="loginLink"
            class="underline"
            @click.prevent="switchElem"
          >
            {{ $t('Log in to Evenemangsbiljetter') }}
          </a>
        </div>
      </form>
    </div>
  </div>
</template>
<script>
import Register from '@vue-storefront/core/compatibility/components/blocks/Auth/Register'
import ButtonFull from 'theme/components/theme/ButtonFull.vue'
import BaseCheckbox from 'theme/components/core/blocks/Form/BaseCheckbox.vue'
import BaseInput from 'theme/components/core/blocks/Form/BaseInput.vue'
import { required, email, minLength, sameAs, alpha } from 'vuelidate/lib/validators'
import { alphaUnicode } from 'theme/helpers/alphaUnicode'
import { Logger } from '@vue-storefront/core/lib/logger'

const HTTP_OK = 200

export default {
  validations: {
    email: {
      required,
      email
    },
    fullName: {
      minLength: minLength(2),
      required
    },
    password: {
      minLength: minLength(8),
      required
    },
    rPassword: {
      required,
      sameAsPassword: sameAs('password')
    },
    conditions: {
      sameAs: sameAs(() => true)
    }
  },
  components: {
    ButtonFull,
    BaseCheckbox,
    BaseInput
  },
  mixins: [Register],
  data () {
    return {
      fullName: '',
      newsletter: false
    }
  },
  computed: {
    conditionsText () {
      const terms = '<a href="/i/kopvillkor" class="fs12 underline lowercase" target="_blank" rel="noopener noreferrer">' + this.$t('Terms and conditions') + '</a>'
      const asterisk = '<span class="asterisk">*</span>'

      return this.$t('I accept {terms}{asterisk}', { terms, asterisk })
    }
  },
  methods: {
    register () {
      if (this.$v.$invalid) {
        this.$v.$touch()
        this.$store.dispatch('notification/spawnNotification', {
          type: 'error',
          message: this.$t('Please fix the validation errors'),
          action1: { label: this.$t('OK') }
        })
        return
      }
      const nameParts = this.fullName.trim().split(' ')
      this.firstName = nameParts[0] || ''
      this.lastName = nameParts.slice(1).join(' ') || this.firstName

      this.callRegister()
    },
    async callRegister () {
      this.$bus.$emit('notification-progress-start', this.$t('Registering the account ...'))

      let result

      try {
        result = await this.$store.dispatch('user/register', {
          email: this.email,
          password: this.password,
          firstname: this.firstName,
          lastname: this.lastName
        })
      } catch (err) {
        this.$bus.$emit('notification-progress-stop')
        this.onFailure({ result: 'Unexpected authorization error. Check your Network conection.' })
        Logger.error(err, 'user')()

        return
      }

      if (result.code !== HTTP_OK) {
        this.$bus.$emit('notification-progress-stop')
        this.onFailure(result)

        if (result.result.includes('password')) {
          this.$refs['password'].setFocus('password')
          this.password = ''
          this.rPassword = ''
        }

        return
      }

      let login

      try {
        login = await this.$store.dispatch('user/login', {
          username: this.email,
          password: this.password
        })
      } catch (err) {
        Logger.error(err, 'user')()
        login = { code: null }
      } finally {
        this.$bus.$emit('notification-progress-stop')
      }

      if (login.code !== HTTP_OK) {
        this.$store.commit('ui/setAuthElem', 'login')
        this.onFailure({ result: 'Your account is ready. Please sign in with a login code to continue.' })

        return
      }

      await this.onSuccess()
      this.close()
    },
    async onSuccess () {
      this.$store.dispatch('google-tag-manager/trackEvent', {
        event: 'register'
      })

      if (this.newsletter) {
        await this.subscribeToNewsletter()
      }

      this.$bus.$emit('user-after-login')
    },
    onFailure (result) {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t(result.result),
        action1: { label: this.$t('OK') }
      })
    },
    async subscribeToNewsletter () {
      if (!this.$v.email.$error) {
        try {
          if (!this.$store.state.user.current) {
            await this.$store.dispatch('newsletter/status', this.email)
          }

          if (!this.isSubscribed) {
            const isSubscribed = await this.$store.dispatch('newsletter/subscribe', this.email)
            this.onSuccessfulSubmission(isSubscribed)
          } else {
            await this.$store.dispatch('notification/spawnNotification', {
              type: 'success',
              message: this.$t('You previously have been subscribed to our newsletter!'),
              action1: { label: this.$t('OK') }
            })
          }
        } catch (error) {
          Logger.error(error)
        }
      }
    },
    onSuccessfulSubmission (isSubscribed) {
      if (isSubscribed) {
        this.$store.dispatch('klaviyo/updateCustomerEmail', { email: this.email })
        this.$store.dispatch('notification/spawnNotification', {
          type: 'success',
          message: this.$t('You have been successfully subscribed to our newsletter!'),
          action1: { label: this.$t('OK') }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/variables/grid';
$asterisk-color: #c00;
$checkbox-label-color: #404040;
$cross-link-color: #6c6c6c;
$link-color: color(primary);
$divider-color: #94949233;
$modal-fullscreen-breakpoint: 600px;
$modal-mobile-gutter: 20px;
$modal-mobile-header-padding: 15px;
$modal-mobile-bottom-padding: 30px;

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 32px;
}

.icon-close-new {
  height: 24px;
  width: 24px;
}

.modal-close {
  cursor: pointer;
}

.button-primary {
  height: 69px;
}

.login-link {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid $divider-color;
  font-size: 16px;
  color: $cross-link-color;

  a {
    font-size: 16px;
    font-weight: 700;
    color: $link-color;
  }
}

.checkbox-label {
  /deep/.checkbox-text,
  /deep/a {
    font-size: 18px;
    line-height: 24px;
    color: $checkbox-label-color;

    .asterisk {
      color: $asterisk-color;
    }
  }
}

@include mobile {
  .login-link {
    line-height: 30px;
  }
}

@media (max-width: $modal-fullscreen-breakpoint) {
  .modal-header {
    padding-top: $modal-mobile-header-padding;
    padding-left: $modal-mobile-gutter;
    padding-right: $modal-mobile-gutter;
  }

  .modal-content {
    padding-left: $modal-mobile-gutter;
    padding-right: $modal-mobile-gutter;
    padding-bottom: $modal-mobile-bottom-padding;
  }
}

@media (max-width: 400px) {
  .modal-header {
    font-size: 20px;
  }

  .button-primary {
    font-size: 20px;
    height: 59px;
  }

  .modal-content {
    padding-top: 10px;
  }

  .icon-close-new {
    height: 18px;
    width: 18px;
  }
}
</style>
